import { RyeconClock } from '@dbt-labs/sourdough';

import DetailTabs, { TabInfo } from '@/components/DetailTabs';
import { useSavedQuery } from '@/hooks/dbtStrategy/useSavedQuery';
import { GenericResourceNode } from '@/hooks/discovery/types';
import { ParsedUniqueId } from '@/typings/parseUniqueId';
import {
  formatAbsoluteLocalDate,
  formatDateCommon,
} from '../../../../../../../util/date';
import {
  getResourceTypeHeaderIcon,
  ResourceDetailsHeader,
  TextIconPair,
} from '../Header';
import { GeneralView } from '../Tabs/GeneralView';
import { QueryExportsView } from '../Tabs/QueryExportsView';

type ResourceParams = {
  uniqueId: ParsedUniqueId;
};

const tabs: TabInfo[] = [{ type: 'general' }, { type: 'queryExports' }];

export const SavedQueryView = ({ uniqueId }: ResourceParams) => {
  const savedQueryQuery = useSavedQuery(uniqueId);

  if (savedQueryQuery.error) return <>{savedQueryQuery.error.stack}</>;
  const savedQuery = (savedQueryQuery.fetching ? null : savedQueryQuery.data) ?? null;

  const lastUpdatedAtUnformatted = savedQuery?.definition?.runGeneratedAt;
  const date = lastUpdatedAtUnformatted
    ? new Date(lastUpdatedAtUnformatted)
    : undefined;
  const updatedAt = date && formatAbsoluteLocalDate(date);
  const updatedAtUtc = date && formatDateCommon(date).utc;

  const headerIcons: TextIconPair[] = [getResourceTypeHeaderIcon('saved_query')];
  if (updatedAt) {
    headerIcons.push({
      ryecon: RyeconClock,
      text: `Definition updated as of ${updatedAt}`,
      tooltip: updatedAtUtc || undefined,
    });
  }

  return (
    <>
      <ResourceDetailsHeader
        uniqueId={uniqueId.raw}
        title={savedQuery?.definition?.name ?? uniqueId.remainder}
        headerIcons={headerIcons}
        packageName={uniqueId.packageName}
        files={[savedQuery?.definition?.filePath]}
        name={savedQuery?.definition?.name}
        resourceType="saved_query"
        updatedDate={date}
      />
      <DetailTabs tabs={tabs} show={!!savedQuery}>
        {(activeTab) => {
          return (
            <>
              {activeTab === 'general' && (
                <GeneralView
                  resource={savedQuery?.definition as GenericResourceNode}
                  uniqueId={uniqueId}
                  description={savedQuery?.definition?.description}
                  meta={null}
                />
              )}
              {activeTab === 'queryExports' && (
                <QueryExportsView resource={savedQuery} />
              )}
            </>
          );
        }}
      </DetailTabs>
    </>
  );
};
